import { Thumbmark } from '@thumbmarkjs/thumbmarkjs'
import Vue from 'vue'

/**
 * Thumbmark 服務
 * 用於生成和管理瀏覽器指紋
 */
class ThumbmarkService {
  constructor() {
    this.thumbmark = null
    this.thumbmarkInstance = null
    this.cookieName = 'thumbmark'
    this.cookieExpireDays = 365 // Cookie 有效期 1 年
  }

  /**
   * 從 Cookie 取得 thumbmark
   * @returns {string|null} thumbmark 字串或 null
   */
  getThumbmarkFromCookie() {
    return Vue.$cookies.get(this.cookieName)
  }

  /**
   * 儲存 thumbmark 到 Cookie
   * @param {string} thumbmark - thumbmark 字串
   */
  setThumbmarkToCookie(thumbmark) {
    // 設定 Cookie,有效期 1 年
    Vue.$cookies.set(this.cookieName, thumbmark, `${this.cookieExpireDays}d`, '/', '', true, 'Lax')
  }

  /**
   * 初始化並取得 thumbmark
   * @returns {Promise<string>} thumbmark 字串
   */
  async getThumbmark() {
    const isSandbox = process.env.NODE_ENV !== 'production'

    if (this.thumbmark) {
      if (isSandbox)
        console.debug('[Thumbmark] Using cached thumbmark:', this.thumbmark)

      return this.thumbmark
    }

    // 先檢查 Cookie 是否已有 thumbmark
    const storedThumbmark = this.getThumbmarkFromCookie()
    if (storedThumbmark) {
      this.thumbmark = storedThumbmark
      if (isSandbox)
        console.debug('[Thumbmark] Loaded from cookie:', this.thumbmark)

      return this.thumbmark
    }

    // 如果沒有,則生成新的 thumbmark
    if (!this.thumbmarkInstance) {
      // 排除不穩定或隱私敏感的 components
      this.thumbmarkInstance = new Thumbmark({
        exclude: [
          'screen', // 螢幕解析度 (會因視窗大小改變)
          'fonts', // 字型列表 (不同裝置差異大)
          'elapsed', // 執行時間 (每次都不同)
          'hardware.jsHeapSizeLimit', // JS 記憶體限制 (可能變動)
          'permissions' // 排除 permissions (不同網站可能有不同的權限狀態)
        ]
      })
      if (isSandbox)
        console.debug('[Thumbmark] ThumbmarkJS instance created with excluded components:', ['screen', 'fonts', 'elapsed', 'hardware.jsHeapSizeLimit', 'permissions'])
    }
    const result = await this.thumbmarkInstance.get()
    this.thumbmark = result.thumbmark // 只取 thumbmark 字串

    if (isSandbox)
      console.debug('[Thumbmark] New thumbmark generated:', this.thumbmark)

    // 儲存到 Cookie
    this.setThumbmarkToCookie(this.thumbmark)

    return this.thumbmark
  }

  /**
   * 取得 thumbmark 和完整 components
   * @returns {Promise<{thumbmark: string, components: Object}>}
   */
  async getThumbmarkWithComponents() {
    const isSandbox = process.env.NODE_ENV !== 'production'

    // 先檢查快取
    if (this.thumbmark) {
      if (isSandbox)
        console.debug('[Thumbmark] Using cached thumbmark:', this.thumbmark)
    }

    // 先檢查 Cookie 是否已有 thumbmark
    const storedThumbmark = this.getThumbmarkFromCookie()
    if (storedThumbmark && !this.thumbmark) {
      this.thumbmark = storedThumbmark
      if (isSandbox)
        console.debug('[Thumbmark] Loaded from cookie:', this.thumbmark)
    }

    // 確保 thumbmarkInstance 已初始化
    if (!this.thumbmarkInstance) {
      this.thumbmarkInstance = new Thumbmark({
        exclude: [
          'screen', // 螢幕解析度 (會因視窗大小改變)
          'fonts', // 字型列表 (不同裝置差異大)
          'elapsed', // 執行時間 (每次都不同)
          'hardware.jsHeapSizeLimit', // JS 記憶體限制 (可能變動)
          'permissions' // 排除 permissions (不同網站可能有不同的權限狀態)
        ]
      })
      if (isSandbox)
        console.debug('[Thumbmark] ThumbmarkJS instance created')
    }

    // 取得完整結果 (包含 thumbmark 和 components)
    const fullResult = await this.thumbmarkInstance.get()
    const thumbmark = fullResult.thumbmark
    const components = fullResult.components

    // 儲存 thumbmark 到快取和 Cookie
    if (!this.thumbmark) {
      this.thumbmark = thumbmark
      this.setThumbmarkToCookie(thumbmark)
    }

    if (isSandbox) {
      console.debug('[Thumbmark] Generated with components:', {
        thumbmark,
        componentsKeys: Object.keys(components),
        componentsSize: `${(new Blob([JSON.stringify(components)]).size / 1024).toFixed(2)} KB`
      })
      console.debug('[Thumbmark] Full components detail:', components)

      // 詳細 debug 資訊 (僅開發環境)
      console.debug('=== Thumbmark Generation (Debug) ===')
      console.debug('Thumbmark:', thumbmark)
      console.debug('Components Keys:', Object.keys(components))
      console.debug('Components Detail:', JSON.stringify(components, null, 2))
      console.debug('=====================================')
    }

    return { thumbmark, components }
  }

  /**
   * 清除儲存的 thumbmark（用於測試或重置）
   */
  clearThumbmark() {
    this.thumbmark = null
    Vue.$cookies.remove(this.cookieName)
  }
}

// 導出單例
export default new ThumbmarkService()